<template>
  <div>
    <div class="section-hero-subpage bg-color-octrary">
      <video autoplay loop muted playsinline width="640" preload="metadata" poster="/frontend/assets/images/og-image.jpg">
        <source src="/frontend/assets/videos/video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <div class="container">
        <div class="paragraph--normal text-uppercase text-color-quinary mb-2">
          <span class="notranslate" translate="no">SELECTED</span> CARS
        </div>
        <h1
          class="heading--0 text-uppercase text-color-quinary border-bottom-quinary padding-bottom-2 margin-bottom-3-lg"
        >
          Selected <span class="fw-light">people</span><br />
          driven by passion<br />
          <span class="fw-light">focused on you</span>
        </h1>
        <div class="row">
          <div class="col-lg-7">
            <p class="paragraph--xlarge text-color-senary">
              Every member of our team is committed to delivering a service
              experience as refined and tailored as the cars we offer.
            </p>
          </div>
        </div>
        <div class="d-lg-none mt-4">
          <a
            href="#contact-section"
            class="btn btn--normal btn--icon--100 btn--icon--100--right btn--primary text-uppercase w-100"
          >
            <Icon icon="material-symbols:expand-circle-down-rounded" />CONTACT
            US
          </a>
        </div>
      </div>
    </div>

    <div class="section-small bg-color-quaternary">
      <div class="container">
        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3"
        >
          Selected headquarters
        </h2>

        <div class="row">
          <div v-if="isLoading" class="col-12 text-center py-5">
            <div class="spinner-border text-light" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          
          <template v-else>
            <div class="col-lg-6 mb-lg-0 mb-4">
              <div class="block-item">
                <div class="paragraph--xxxlarge text-color-quinary lh-1 mb-3">
                  Email
                </div>
                <div class="d-flex align-items-center gap-3">
                  <div class="icon-circle">
                    <Icon icon="ic:sharp-email" class="iconify phonis" />
                  </div>
                  <a
                    :href="'mailto:' + (headquarters?.email || 'info@selected.sk')"
                    class="paragraph--xlarge text-color-quinary"
                    >{{ headquarters?.email || 'info@selected.sk' }}</a
                  >
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="block-item">
                <div class="paragraph--xxxlarge text-color-quinary lh-1 mb-3">
                  Phone and Whatsapp
                </div>
                <div class="d-flex align-items-center gap-3">
                  <div class="icon-circle">
                    <Icon icon="ic:baseline-local-phone" class="iconify phonis" />
                  </div>
                  <a
                    :href="'tel:' + (headquarters?.phone || '+421 949 006 016')"
                    class="paragraph--xlarge text-color-quinary"
                    >{{ headquarters?.phone || '+421 949 006 016' }}</a
                  >
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="section-small bg-color-quaternary">
      <div class="container">
        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3"
        >
          Sales
        </h2>

        <div class="row align-items-center">
          <div v-if="isLoading" class="col-12 text-center py-5">
            <div class="spinner-border text-light" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          
          <template v-else-if="agents.length === 0">
            <div class="col-12 text-center">
              <p class="paragraph--large text-color-quinary">No sales agents available at the moment.</p>
            </div>
          </template>
          
          <template v-else>
            <div v-for="agent in agents" :key="agent.id" class="col-lg-4 col-md-6 mb-lg-0 mb-5">
              <div class="contact-item">
                <div class="contact-item--img contact-item--img--custom mb-3">
                  <img :src="getAgentImage(agent)" :alt="agent.name" />
                </div>

                <div class="paragraph--xxxlarge text-color-quinary">
                  {{ agent.name }}
                </div>
                <div class="text-color-senary">{{ agent.position || 'Sales Executive' }}</div>
                <div v-if="agent.phone" class="d-flex align-items-center gap-3 mb-3 mt-4">
                  <div class="icon-circle">
                    <Icon icon="ic:baseline-local-phone" />
                  </div>
                  <span class="paragraph--xlarge text-color-quinary">{{ agent.phone }}</span>
                </div>

                <div v-if="agent.email" class="d-flex align-items-center gap-3">
                  <div class="icon-circle">
                    <Icon icon="ic:sharp-email" />
                  </div>
                  <span class="paragraph--xlarge text-color-quinary">{{ agent.email }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="section-small bg-color-quaternary">
      <div class="container">
        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3"
        >
          DEPARTMENTS
        </h2>

        <div class="row">
          <div v-if="isLoading" class="col-12 text-center py-5">
            <div class="spinner-border text-light" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          
          <template v-else-if="departments.length === 0">
            <div class="col-12 text-center">
              <p class="paragraph--large text-color-quinary">No departments available at the moment.</p>
            </div>
          </template>
          
          <template v-else>
            <div v-for="department in departments" :key="department.id" class="col-xl-4 col-lg-6 mb-xl-0 mb-4">
              <div class="block-item">
                <div class="paragraph--xxxlarge text-color-quinary lh-1 mb-3">
                  {{ department.name }}
                </div>
                <p class="paragraph--normal text-color-senary">
                  {{ department.description || 'Our team is ready to assist you with any inquiries.' }}
                </p>
                <div v-if="department.email" class="d-flex align-items-center gap-3 mt-4">
                  <div class="icon-circle">
                    <Icon icon="ic:sharp-email" />
                  </div>
                  <a
                    :href="'mailto:' + department.email"
                    class="paragraph--xlarge text-color-quinary"
                    >{{ department.email }}</a
                  >
                </div>
                <div v-if="department.phone" class="d-flex align-items-center gap-3 mt-3">
                  <div class="icon-circle">
                    <Icon icon="ic:baseline-local-phone" />
                  </div>
                  <a
                    :href="'tel:' + department.phone"
                    class="paragraph--xlarge text-color-quinary"
                    >{{ department.phone }}</a
                  >
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div id="contact-section" class="section-small bg-color-quaternary">
      <div class="container">
        <h2
          class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3"
        >
          SEND US A MESSAGE
        </h2>

        <div class="row">
          <div class="col-lg-8 mx-auto">
            <form @submit.prevent="submitForm" class="contact-form">
              <div class="row">
                <div class="col-md-6 mb-4">
                  <label for="name" class="form-label text-color-senary paragraph--small wide text-uppercase">Name</label>
                  <input
                    type="text"
                    class="form-control bg-color-tertiary text-color-quinary border-0"
                    id="name"
                    v-model="form.name"
                    placeholder="Your full name"
                    :class="{'is-invalid': errors.name}"
                  >
                  <div class="invalid-feedback" v-if="errors.name">
                    {{ errors.name }}
                  </div>
                </div>
                <div class="col-md-6 mb-4">
                  <label for="email" class="form-label text-color-senary paragraph--small wide text-uppercase">Email</label>
                  <input
                    type="email"
                    class="form-control bg-color-tertiary text-color-quinary border-0"
                    id="email"
                    v-model="form.email"
                    placeholder="your@email.com"
                    :class="{'is-invalid': errors.email}"
                  >
                  <div class="invalid-feedback" v-if="errors.email">
                    {{ errors.email }}
                  </div>
                </div>
              </div>

              <div class="mb-4">
                <label for="phone" class="form-label text-color-senary paragraph--small wide text-uppercase">Phone</label>
                <input
                  type="tel"
                  class="form-control bg-color-tertiary text-color-quinary border-0"
                  id="phone"
                  v-model="form.phone"
                  placeholder="+421 ..."
                  :class="{'is-invalid': errors.phone}"
                >
                <div class="invalid-feedback" v-if="errors.phone">
                  {{ errors.phone }}
                </div>
              </div>

              <div class="mb-4">
                <label for="message" class="form-label text-color-senary paragraph--small wide text-uppercase">Message</label>
                <textarea
                  class="form-control bg-color-tertiary text-color-quinary border-0"
                  id="message"
                  rows="5"
                  v-model="form.message"
                  placeholder="Tell us what you are looking for..."
                  :class="{'is-invalid': errors.message}"
                ></textarea>
                <div class="invalid-feedback" v-if="errors.message">
                  {{ errors.message }}
                </div>
              </div>

              <button type="submit" class="btn btn--normal btn--primary w-100" :disabled="formLoading">
                <span v-if="formLoading">SENDING...</span>
                <span v-else>SEND MESSAGE</span>
              </button>

              <div class="alert alert-success mt-4" v-if="successMessage">
                {{ successMessage }}
              </div>
              <div class="alert alert-danger mt-4" v-if="formError">
                {{ formError }}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <AppMap></AppMap> 

    <AppMarquee />
  </div>
</template>

<script>
import AppMarquee from '../components/AppMarquee.vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';
import AppMap from '../components/AppMap.vue';

export default {
  components: {
    AppMarquee,
    Icon,
    AppMap
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        phone: '',
        message: ''
      },
      errors: {},
      formLoading: false,
      formError: '',
      successMessage: '',
      headquarters: null,
      departments: [],
      agents: [],
      isLoading: true
    };
  },
  mounted() {
    import('../seo.js').then(({ setPageSeo }) => setPageSeo({
      title: 'Contact Selected Cars | WhatsApp, Telegram & Email | International Inquiries',
      description: 'Contact Selected Cars for international car export inquiries. WhatsApp +421 949 006 016, Telegram @selectedslovakia, email info@selected.sk. We respond worldwide within hours. Showroom in Bratislava, Slovakia by appointment.',
      keywords: 'contact selected cars, selected cars whatsapp international, car export inquiry europe, selected cars telegram, selected cars email, european car dealer contact, international car buyer inquiry, car export quote request, contact european car broker, selected cars phone number',
      breadcrumbs: [
        { name: 'Home', url: '/' },
        { name: 'Contact', url: '/contacts' }
      ],
      schema: {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Selected Cars",
        "url": "https://selected.sk/contacts",
        "mainEntity": {
          "@type": "AutoDealer",
          "name": "Selected Cars",
          "telephone": "+421949006016",
          "email": "info@selected.sk",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Devinska Nova Ves 7467, Squarebizz Bory",
            "addressLocality": "Bratislava",
            "postalCode": "841 07",
            "addressCountry": "SK"
          }
        }
      }
    }));
    this.fetchContactData();
  },
  methods: {
    async fetchContactData() {
      this.isLoading = true;
      try {
        // Fetch contact data (agents and headquarters)
        const response = await axios.get('/api/visible-agents');
        console.log('Contact data:', response.data);
        
        // Set agents from the response
        this.agents = response.data.agents || [];
        
        // Set headquarters from the response or use fallback values
        this.headquarters = response.data.headquarters || {
          phone: '+421 949 006 016',
          email: 'info@selected.sk',
          address: 'Selected Cars Headquarters'
        };
        
        // Set departments from the response
        this.departments = response.data.departments || [];
      } catch (error) {
        console.error('Error fetching contact data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    getAgentImage(agent) {
      // Check if agent has a primary image
      if (agent.primary_image && agent.primary_image.url) {
        return agent.primary_image.url;
      }
      // Check if agent has any images and return the first one
      else if (agent.images && agent.images.length > 0 && agent.images[0].path) {
        return '/storage/' + agent.images[0].path;
      }
      // Fallback to default image
      return '/frontend/assets/images/person.png';
    },
    
    validateForm() {
      this.errors = {};
      
      if (!this.form.name) {
        this.errors.name = 'Name is required';
      }
      
      if (!this.form.email) {
        this.errors.email = 'Email is required';
      } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
        this.errors.email = 'Please enter a valid email address';
      }
      
      if (!this.form.phone) {
        this.errors.phone = 'Phone number is required';
      }
      
      if (!this.form.message) {
        this.errors.message = 'Message is required';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.formLoading = true;
      this.formError = '';
      this.successMessage = '';

      try {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        await axios.post('/api/contact', this.form);

        this.successMessage = 'Thank you for your message! We will get back to you shortly.';
        this.form = {
          name: '',
          email: '',
          phone: '',
          message: ''
        };
      } catch (error) {
        console.error('Error submitting form:', error);
        this.formError = 'An error occurred while sending your message. Please try again.';
      } finally {
        this.formLoading = false;
      }
    }
  }
};
</script>

<style scoped>

</style>
